import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { createAxiosMutation } from "@/hooks/react-query";
import { instance } from "@/lib/hooks";

import {
  HardwareOrder,
  HardwareOrderListFilters,
  NetsuiteDraftOrderRequest,
  NetsuiteDraftResponseData,
  NetsuiteProductsResponseData,
  NetsuiteSubmitOrderRequest,
  NetsuiteSubmitResponse,
} from "@/types";

// List Hardware Orders
interface HardwareOrderPaginatedResult {
  records: HardwareOrder[];
  total_records: number;
  total_pages: number;
  current_page: number;
}

export const useHardwareOrdersQuery = (
  filters: HardwareOrderListFilters,
  options?: UseQueryOptions<HardwareOrderPaginatedResult>
) => {
  return useQuery<HardwareOrderPaginatedResult>({
    queryKey: ["hardware-orders", filters],
    queryFn: async () => {
      const { data } = await instance.get("/hardware-orders", {
        params: filters,
      });
      return data;
    },
    ...options,
  });
};

// Get Single Hardware Order
export const useHardwareOrderQuery = (
  hardwareOrderId: string | null | undefined,
  options?: UseQueryOptions<HardwareOrder>
) => {
  return useQuery<HardwareOrder>({
    queryKey: ["hardware-order", hardwareOrderId],
    queryFn: async () => {
      const { data } = await instance.get(
        `/hardware-orders/${hardwareOrderId}`
      );
      return data;
    },
    enabled: !!hardwareOrderId,
    ...options,
  });
};

// NetSuite Products
export const useNetsuiteProductsQuery = (
  groupId: number | null | undefined,
  options?: UseQueryOptions<NetsuiteProductsResponseData>
) => {
  return useQuery<NetsuiteProductsResponseData>({
    queryKey: ["netsuite-products", groupId],
    queryFn: async () => {
      const { data } = await instance.get(
        "/hardware-orders/netsuite/products",
        {
          params: { group_id: groupId },
        }
      );
      return data?.response;
    },
    enabled: !!groupId && groupId > 0,
    ...options,
  });
};

// NetSuite Draft Order
export const useNetsuiteDraftOrderMutation = createAxiosMutation<
  NetsuiteDraftResponseData,
  Error,
  NetsuiteDraftOrderRequest
>(async (requestData) =>
  instance.post("/hardware-orders/netsuite/draft", requestData)
);

// NetSuite Submit Order
export const useNetsuiteSubmitOrderMutation = createAxiosMutation<
  NetsuiteSubmitResponse,
  Error,
  NetsuiteSubmitOrderRequest
>(async (requestData) =>
  instance.post("/hardware-orders/netsuite/submit", requestData)
);
